import escapeRegExp from 'lodash/escapeRegExp'
import filter from 'lodash/filter'
import {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addParticipant, removeParticipant } from 'reduxes/slices/topics/actions'

import {
  getApiContacts,
  getApiPublicContacts
} from '../reduxes/slices/api/selectors'
import { getCurrentHandle } from '../reduxes/slices/current/selectors'
import { makeGetParticipantsForTopic } from '../reduxes/slices/participants/selectors'
import { useFormMemory } from './use_form_memory'
import {useAxios} from "./use_axios";
import {ContactsContext} from "../contexts/contacts_context.ts"
import {useSelectorDelegateStore} from "../reduxes/stores/root_store/hooks";

const initialFormInputValues = { nameOrEmail: '' }

export function useTopicPeople(
  topic,
  suggestAllInitially,
  customHandleSubmit = null,
  addedHandlesFromProps = null,
  newTopic = false,
) {
  const dispatch = useDispatch()
  const axios = useAxios()
  const formId = (newTopic && -1) || topic?.id
  const { formInputValues, setInputValue: setFormInputValue } = useFormMemory(
    formId,
    initialFormInputValues,
  )
  const inputValue = formInputValues?.nameOrEmail
  const [suggestedContacts, setSuggestedContacts] = useState([])
  const [addedBuffer, setAddedBuffer] = useState([])
  const [removedBuffer, setRemovedBuffer] = useState([])

  const allContacts = useSelector(getApiContacts)
  const allPublicContacts = useSelectorDelegateStore(getApiPublicContacts)
  const getParticipantsForTopic = useMemo(makeGetParticipantsForTopic, [])
  const participants = useSelector((state) =>
    getParticipantsForTopic(state, topic),
  )
  const currentHandle = useSelector(getCurrentHandle)
  const {readContacts, setReadContacts} = useContext(ContactsContext)

  const addedHandles = useMemo(() => {
    if (addedHandlesFromProps) {
      return addedHandlesFromProps
    }
    return participants.reduce((result, participant) => {
      result.push(participant.handle)
      return result
    }, [])
  }, [addedHandlesFromProps, participants])

  const topicId = topic?.id

  const setInputValue = useCallback(
    (value) => {
      setFormInputValue('nameOrEmail', value)
    },
    [setFormInputValue],
  )

  useEffect(() => {
    setInputValue('')
    setAddedBuffer([])
    setRemovedBuffer([])
  }, [setInputValue, topicId])

  const getAddedHandles = useCallback(() => {
    const buffersDiff = addedBuffer.filter((x) => !removedBuffer.includes(x))
    return [...addedHandles, ...buffersDiff]
  }, [addedBuffer, addedHandles, removedBuffer])

  const getFilter = useCallback(
    (addedHandles) => {
      const re = new RegExp(escapeRegExp(inputValue), 'i')
      return (contact) => {
        const handle = contact.handle
        const isMatch =
          handle && !addedHandles.includes(handle) && handle !== currentHandle
        if (suggestAllInitially && inputValue === '') {
          return isMatch
        }
        return isMatch && re.test(handle)
      }
    },
    [currentHandle, inputValue, suggestAllInitially],
  )

  useMemo(() => {
    if (!suggestAllInitially && inputValue === '') {
      setSuggestedContacts([])
      return
    }
    const addedHandlesWithBuffers = getAddedHandles()

    const isMatch = getFilter(addedHandlesWithBuffers)

    const contactMatches = allContacts
      ? filter(Object.values(allContacts), isMatch)
      : []
    const result = contactMatches
      .sort((contactA, contactB) => {
        return (
          Date.parse(contactB.last_messaged_at || contactB.created_at) -
          Date.parse(contactA.last_messaged_at || contactA.created_at)
        )
      })
      .slice(0, 5)

    if (result.length < 5) {
      let resultHandles = new Set(result.map(e => e.handle.toLowerCase()))
      const publicContactMatches = (allPublicContacts
        ? filter(Object.values(allPublicContacts), isMatch)
        : []).sort((contactA, contactB) => {
        return contactA.handle.toUpperCase() >
        contactB.handle.toUpperCase()
          ? 1
          : -1
      })

      for (let pc of publicContactMatches) {
        let handle = pc.handle.toLowerCase()
        if (!resultHandles.has(handle)) {
          result.push(pc)
          resultHandles.add(handle)

          if (result.length >= 5) {
            break
          }
        }
      }
    }

    setSuggestedContacts(result)
  }, [allContacts, allPublicContacts, getAddedHandles, getFilter, inputValue, suggestAllInitially])

  const handleInputChange = useCallback(
    (event) => {
      const value = event.currentTarget.value
      if (value.includes('@')) {
        value.trim()
      }
      setInputValue(value)

      if (!readContacts && setReadContacts) {
        const readContactsIos = window?.webkit?.messageHandlers?.readContacts
        if (readContactsIos) {
          readContactsIos.postMessage(readContacts)
        }
        setReadContacts(true)
      }
    },
    [setInputValue, readContacts, setReadContacts],
  )

  const handleSubmit = useCallback(
    (handle) => {
      let promise
      if (!customHandleSubmit) {
        setAddedBuffer((prev) => {
          return [...prev, handle]
        })
        promise = dispatch(axios(addParticipant(topic, handle))).finally(() => {
          setAddedBuffer((prevState) => {
            const index = prevState.indexOf(handle)
            if (index !== -1) {
              return [
                ...prevState.slice(0, index),
                ...prevState.slice(index + 1),
              ]
            } else {
              return prevState
            }
          })
        })
      } else {
        customHandleSubmit(handle)
        setInputValue('')
      }

      return promise || Promise.resolve()
    },
    [axios, customHandleSubmit, dispatch, setInputValue, topic],
  )

  const addContactByEmail = useCallback(
    (handle) => {
      return handleSubmit(handle)
        .then(() => {
          setInputValue('')
        })
        .catch(() => {})
    },
    [handleSubmit, setInputValue],
  )

  const addSuggestedContact = useCallback(
    (handle) => {
      const inputValueSnapshot = inputValue
      if (suggestedContacts.length === 1) {
        setInputValue('')
      }
      return handleSubmit(handle).catch(() => {
        setInputValue(inputValueSnapshot)
      })
    },
    [handleSubmit, inputValue, setInputValue, suggestedContacts.length],
  )

  const handleRemove = useCallback(
    (participant) => {
      if (!participant) {
        return
      }
      const { id, handle } = participant
      setRemovedBuffer((prevState) => {
        return [...prevState, handle]
      })
      return dispatch(axios(removeParticipant(topic.id, id))).finally((result) => {
        setRemovedBuffer((prevState) => {
          const index = prevState.indexOf(handle)
          if (index !== -1) {
            return [...prevState.slice(0, index), ...prevState.slice(index + 1)]
          } else {
            return prevState
          }
        })
        return Promise.resolve(result)
      })
    },
    [axios, dispatch, topic?.id],
  )

  return {
    inputValue,
    setInputValue,
    handleInputChange,
    addContactByEmail,
    addSuggestedContact,
    handleRemove,
    suggestedContacts,
    participants,
  }
}
